import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import {
    createPropFilter,
    RenderTarget,
    WebLink,
    WebLinkProps,
} from '@news-mono/web-common'
import { StyledArticleVideoContainer } from '../../publication/ArticleVideo/ArticleVideo.styled'
import { colors, fonts, metrics } from '../../__styling'
import { breakpoint } from '../../__styling/style-functions/breakpoint'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { RightArrow } from './assets/RightArrow'

interface StyledHeroContainerProps {
    renderTarget?: RenderTarget
    isArticle?: boolean
}
export const StyledHeroContainer = styled('div')<StyledHeroContainerProps>(
    ({ renderTarget, isArticle }) => ({
        display: 'grid',
        gridTemplateRows: 'auto auto',
        gridRowGap: calcRem(24),
        gridTemplateColumns: '1fr',
        alignItems: 'self-start',
        marginBottom: calcRem(metrics.sevennews.margins.lg),
        textDecoration: 'none',
        color: 'inherit',

        [breakpoint('md')]: {
            gridTemplateColumns: isArticle
                ? '45% calc(55% - 1rem)'
                : '40% calc(60% - 2.25rem)',
            gridTemplateRows: 'auto',
            columnGap: isArticle ? calcRem(18) : calcRem(36),
            ['&.hasBorder']: {
                borderBottom: '1px solid rgba(0,0,0,0.1)',
            },
        },
        '& [class*="StyledFigcaption"]': {
            paddingBottom: 0,
            marginTop: 0,
        },

        paddingBottom: renderTarget === 'amp' ? calcRem(20) : 'inherit',
        [`${StyledArticleVideoContainer}`]: {
            marginBottom: 0,
        },
    }),
)

interface StyledBylineWrapProps {
    isArticle?: boolean
}

export const StyledBylineWrap = styled('div')<StyledBylineWrapProps>(
    ({ isArticle }) => ({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center,',
        marginBottom: calcRem(16),
        [breakpoint('md')]: {
            marginBottom: 0,
        },
    }),
)

// currently only on article pages
export const StyledBylineSectionContainer = styled('div')({
    marginBottom: 0,
    [breakpoint('sm')]: {
        marginBottom: calcRem(16),
    },
    [breakpoint('md')]: {
        marginBottom: calcRem(metrics.sevennews.margins.lg),
        marginTop: calcRem(24),
    },
})

export const StyledLastUpdated = styled('span')({
    fontSize: calcRem(12),
    marginTop: calcRem(9),
    color: colors.sevennews.dustyGray,
})

interface StyledTextWrapProps {
    isArticle?: boolean
}
export const StyledTextWrap = styled('div')<StyledTextWrapProps>(
    ({ isArticle }) => ({
        [breakpoint('md')]: {
            marginRight: isArticle ? calcRem(50) : 0,
            marginLeft: isArticle ? calcRem(50) : 0,
            paddingLeft: calcRem(32),
            paddingRight: calcRem(32),
            gridRow: 1,
            height: '100%',
            justifyContent: 'space-between',
        },
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'inherit',
        gridRow: isArticle ? 1 : 2,
    }),
)

export const StyledButtonWrap = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    marginTop: calcRem(16),
})

export const StyledTitle = styled('h1')({
    fontWeight: 700,
    fontFamily: fonts.sevennews.headingAlternative,
    fontSize: calcRem(25),
    marginBottom: calcRem(16),
    lineHeight: '29px',
    textAlign: 'center',
    marginTop: 0,
    [breakpoint('md')]: {
        fontSize: calcRem(36),
        lineHeight: '39px',
        marginTop: 0,
    },
    [breakpoint('sm')]: {
        fontSize: calcRem(30),
        lineHeight: '39px',
    },
})

export const StyledLiveStatus = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.brand,
    fontSize: calcRem(24),
    fontFamily: theme.fonts.headingAlternative,
    fontWeight: 300,
}))

export const StyledTeaserArrow = styled(RightArrow)({
    marginLeft: calcRem(5),
    position: 'relative',
})

export const StyledWebLink = styled(WebLink)({
    textDecoration: 'none',
    color: 'inherit',
})

interface StyledTeaserProps {
    isLanding?: boolean | false
}

export const StyledTeaser = styled('div')<StyledTeaserProps>(
    ({ isLanding }) => ({
        margin: 0,
        fontWeight: 300,
        fontFamily: fonts.sevennews.headingAlternative,
        fontSize: calcRem(20),
        textAlign: 'center',
        marginBottom: calcRem(metrics.sevennews.margins.lg),
        lineHeight: '25px',
        display: isLanding ? 'none' : 'block',
        [breakpoint('sm')]: {
            marginBottom: calcRem(24),
        },
        [breakpoint('md')]: {
            marginBottom: 'inherit',
            display: 'block',
        },
    }),
)
interface StyledHrProps {
    removeMarginBottom: boolean
    isArticle?: boolean | false
}
export const StyledHr = styled('hr', {
    shouldForwardProp: createPropFilter<StyledHrProps>()([
        'removeMarginBottom',
        'isArticle',
    ]),
})<StyledHrProps>(({ removeMarginBottom, isArticle }) => ({
    display: 'block',
    height: '2px',
    width: '100%',
    opacity: 1,
    background: tokens.sevennews.colors.palette.platinum,
    marginBottom: removeMarginBottom ? 0 : calcRem(16),
    marginTop: 0,
    border: 'none',
    [breakpoint('sm')]: {
        display: 'block',
    },
    [breakpoint('md')]: {
        marginBottom: removeMarginBottom ? 0 : calcRem(8),
    },
}))
export const StyledButton = styled(WebLink)({
    background: '#272626',
    borderRadius: '3px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    padding: calcRem(8, 30),
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
})

const PulseRing = keyframes`
0% {
    transform:  scale(.33);
  }
  80%, 100% {
    opacity: 0
  }
`
const PulseDot = keyframes`
    0% {
        transform: scale(.8);
    }
    50% {
        transform: scale(1);
    }
    100% {
        transform: scale(.8);
    }
`

export const StyledPulsatingDotLogo = styled('div')({
    background: '#E6001E',
    height: calcRem(5),
    width: calcRem(5),
    transform: 'translateX(-0%) translateY(-0%)',
    marginBottom: 0,
    marginRight: calcRem(11),
    borderRadius: '50%',
    '&::before': {
        content: '""',
        position: 'relative',
        display: 'block',
        width: '300%',
        height: '300%',
        boxSizing: 'border-box',
        marginLeft: '-100%',
        marginTop: '-100%',
        borderRadius: calcRem(45),
        backgroundColor: '#E6001E',
        animation: `${PulseRing} 1.25s cubic-bezier(0.215, 0.61, 0.355, 1) infinite`,
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        left: '0',
        top: '0',
        display: 'block',
        width: '100%',
        height: '100%',
        backgroundColor: '#E6001E',
        borderRadius: calcRem(15),
        boxShadow: '0 0 8px rgba(0,0,0,.3)',
        animation: `${PulseDot} 1.25s cubic-bezier(0.455, 0.03, 0.515, 0.955) -.4s infinite`,
    },
})

export const StyledCardMediaWrap = styled('div')<StyledTextWrapProps>(
    ({ isArticle }) => ({
        gridRow: isArticle ? 2 : 1,
        [breakpoint('md')]: {
            gridRow: 1,
            alignSelf: 'center',
            display: 'grid',
        },
        '& [class*="StyledMedia"]': {
            marginBottom: 0,
        },
    }),
)

export const StyledArticleByline = styled('div')({
    fontSize: calcRem(12),
    color: colors.sevennews.dustyGray,
    textAlign: 'center',
    fontFamily: fonts.sevennews.headingAlternative,
})

export const StyledArticleBylineLink = styled(WebLink)<WebLinkProps>(
    ({ theme }) => ({
        fontFamily: fonts.sevennews.headingAlternative,
        textDecoration: 'none',
        color: theme.sectionValues.primaryColor,
    }),
)

export const StyledArticleHeader = styled('h2')(({ theme }) => ({
    [breakpoint('md')]: {
        marginBottom: calcRem(8),
    },
    display: 'block',
    color: theme.sectionValues.primaryColor,
    textAlign: 'center',
    fontSize: calcRem(36),
    lineHeight: calcRem(34),
    fontFamily: fonts.sevennews.headingAlternative,
    fontWeight: 400,
    textTransform: 'uppercase',
}))

export const StyledArticleBylineAuthor = styled('span')({
    color: '#000',
    fontFamily: fonts.sevennews.headingAlternative,
})

export const StyledArticleBylineAuthorLink = styled(WebLink)({
    color: '#000',
    textDecoration: 'none',
    fontFamily: fonts.sevennews.headingAlternative,
})

interface StyledSharingWrapProp {
    className?: string
}

export const StyledSharingWrap = styled('span', {
    shouldForwardProp: createPropFilter<StyledSharingWrapProp>()(['className']),
})<StyledSharingWrapProp>(({ theme }) => ({
    color: theme.sectionValues.primaryColor,
    fontWeight: 500,
    fontSize: calcRem(12),
    display: 'flex',
    justifyContent: 'center',

    [breakpoint('md')]: {
        marginBottom: calcRem(0),
    },

    ['.SNEntSharing']: {
        marginTop: calcRem(0),
        marginBottom: calcRem(0),
        [breakpoint('md')]: {
            marginLeft: calcRem(25),
        },

        [`.gtm-facebook-share-button svg`]: {
            top: '50%',
            transform: `translate(-57%, -50%)`,
        },
    },

    ['.gtm-facebook-share-button, .gtm-twitter-share-button, .gtm-email-share-button, .gtm-clipboard-share-button']:
        {
            backgroundColor: 'none',
            ['svg']: {
                fill: theme.sectionValues.primaryColor,
            },
            ['&:hover']: {
                backgroundColor: theme.sectionValues.primaryColor,
                borderColor: theme.sectionValues.primaryColor,
                ['svg']: {
                    fill: '#fff',
                },
            },
        },
}))
export const StyledSharingShadow = styled('div')({
    display: 'flex',
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: `1px 1px 6px rgba(0, 0, 0, 0.16)`,
    padding: calcRem(10, 55),

    [breakpoint('md')]: {
        padding: calcRem(0, 55),
    },
    ['> span']: {
        display: 'none',
        [breakpoint('md')]: {
            display: 'inline-block',
        },
    },
})
